import { observer } from 'mobx-react-lite'
import { type ReactElement, useEffect, useRef } from 'react'
import { GlobalSearchDialog } from './GlobalSearchDialog'
import { onOpenGlobalSearch, openGlobalSearch } from './globalSearchBus'
import { usePaletteNav } from './usePaletteNav'
import { isEditableTarget, isGlobalSearchShortcut } from './shortcut'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { closeModal, showModal } from 'app/lib/modal'
import type { UserJson } from 'app/types/entities'
import { useUser } from 'app/utils'

/**
 * Mounted once in the app shell. Reads the toggle and the signed-in user; the host below
 * holds the page rows so a reopen does not refetch the nav tree.
 */
export const GlobalSearch = observer(function GlobalSearch(): ReactElement | null {
  const enabled = useFeatureToggle(`global-search`)
  const user = useUser()
  if (!enabled || !user) return null
  return <GlobalSearchHost user={user as UserJson} />
})

/**
 * Owns the Ctrl+K listener and the page rows, and opens the palette through the app's
 * modal (`showModal`), which supplies the backdrop, Escape handling, and focus. Renders
 * nothing itself. Every open request, keyboard or trigger button, travels the bus: the
 * host opens the modal when it is closed, and the mounted dialog refocuses its input when
 * it is already open.
 */
const GlobalSearchHost = observer(function GlobalSearchHost({ user }: { user: UserJson }): null {
  const { pageRows, locationCount } = usePaletteNav(user)
  const openRef = useRef(false)
  // showModal snapshots the element it is given, so read the latest rows at open time.
  const navRef = useRef({ pageRows, showLocation: true })
  // Undefined is org level, where rows arrive from many locations and the name always helps.
  navRef.current = { pageRows, showLocation: locationCount === undefined || locationCount > 1 }

  useEffect(() => {
    const open = () => {
      if (openRef.current) return
      openRef.current = true
      showModal(
        <GlobalSearchDialog
          onClose={closeModal}
          pageRows={navRef.current.pageRows}
          showLocation={navRef.current.showLocation}
        />,
        {
          useRadix: true,
          isFullScreen: false,
          hideTitle: true,
          closable: false,
          removePadding: true,
          overflowHidden: true,
          // RadixModal shrink-wraps the panel to its rows, so state the width: the viewport
          // minus the container's own px-6 gutter, so a phone is not edge to edge.
          panelClassName: `w-[calc(100vw-3rem)] max-w-[640px] rounded-lg`,
          // Centered, a phone keyboard covers the results the box is producing.
          containerClassName: `items-start pt-[calc(4.5rem+env(safe-area-inset-top))] md:items-center md:pt-0`,
          onClose: () => {
            openRef.current = false
          },
        },
      )
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isGlobalSearchShortcut(event) || isEditableTarget(event.target)) return
      event.preventDefault()
      openGlobalSearch()
    }
    window.addEventListener(`keydown`, onKeyDown)
    const unsubscribe = onOpenGlobalSearch(open)
    return () => {
      window.removeEventListener(`keydown`, onKeyDown)
      unsubscribe()
    }
  }, [])

  return null
})
